import isUndefined from 'lodash/isUndefined';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {SEARCH_TYPE} from '../search-prop-types';
import CloudinaryUtils from 'utils/cloudinary';
import Alert from '@spothero/ui/alert';
import Image from '@spothero/ui/image';
import WithConfig from 'config/WithConfig';
import ProximityInformation from 'spot-details/details/ProximityInformation';
import AmenityIconList from 'spot-details/details/AmenityIconList';

class SpotListItemRow extends Component {
    static propTypes = {
        spot: PropTypes.object.isRequired,
        updateSelectedSpot: PropTypes.func.isRequired,
        isMonthly: PropTypes.bool,
        showAmenities: PropTypes.bool,
        searchType: SEARCH_TYPE,
        cloudinary: PropTypes.object,
        staticBaseUrl: PropTypes.string
    };
    static defaultProps = {
        showAmenities: true
    };
    state = {
        hovered: false
    };

    constructor(props) {
        super(props);

        const {
            cloudinary,
            spot,
            staticBaseUrl
        } = props;

        this._IMAGE_WIDTH = 112;
        this._IMAGE_HEIGHT = 101;
        this._FORMATTED_IMAGE = (!isUndefined(spot.facility.images[0]))
            ? cloudinary.url(
                spot.facility.images[0].id,
                CloudinaryUtils.transformations(this._IMAGE_WIDTH, this._IMAGE_HEIGHT)
            )
            : `${staticBaseUrl}img/missing-image.png`;
    }

    _onSpotHover = ({hovered, spotId}) => {
        if (spotId === this.props.spot.spotId) {
            this.setState({
                hovered
            });
        }
    }

    _onShowDetailsClick = evt => {
        evt.preventDefault();

        const {
            spot,
            updateSelectedSpot
        } = this.props;

        updateSelectedSpot({
            spot,
            referrer: 'list'
        });
    }

    _renderControls = () => {
        const {
            spot: {
                selectedRate: {
                    price_formatted: priceFormatted
                }
            }
        } = this.props;

        return (
            <div
                className="price"
                dangerouslySetInnerHTML={{__html: priceFormatted}}
            />
        );
    }

    _renderOfferSchema = () => {
        const {
            spot: {
                selectedRate: {
                    duration,
                    price,
                    starts,
                    ends,
                    fullUrl
                },
                title
            }
        } = this.props;
        const description = `Reserved Parking at ${title} for ${duration} Hour${(duration === 1) ? '' : 's'}`;

        return (
            <span>
                <meta
                    itemProp="name"
                    content="Parking"
                />
                <meta
                    itemProp="url"
                    content={fullUrl}
                />
                <meta
                    itemProp="description"
                    content={description}
                />
                <meta
                    itemProp="priceCurrency"
                    content="USD"
                />
                <meta
                    itemProp="price"
                    content={`$${price / 100}`}
                />
                <meta
                    itemProp="availability"
                    content="http://schema.org/InStock"
                />
                <meta
                    itemProp="validFrom"
                    content={starts}
                />
                <meta
                    itemProp="validThrough"
                    content={ends}
                />
                <span
                    itemProp="offeredBy"
                    itemScope
                    itemType="http://schema.org/Organization"
                >
                    <meta
                        itemProp="name"
                        content="SpotHero"
                    />
                    <meta
                        itemProp="url"
                        content="https://spothero.com"
                    />
                </span>
            </span>
        );
    }

    _renderSpotSchema = () => {
        const {
            spot: {
                spot_url: spotUrl,
                facility: {
                    street_address: address,
                    city,
                    state
                },
                latitude,
                longitude
            }
        } = this.props;

        return (
            <span>
                <span
                    itemProp="address"
                    itemScope
                    itemType="http://schema.org/PostalAddress"
                >
                    <meta
                        itemProp="streetAddress"
                        content={address}
                    />
                    <meta
                        itemProp="addressLocality"
                        content={city}
                    />
                    <meta
                        itemProp="addressRegion"
                        content={state}
                    />
                </span>
                <span
                    itemProp="geo"
                    itemScope
                    itemType="http://schema.org/GeoCoordinates"
                >
                    <meta
                        itemProp="latitude"
                        content={latitude}
                    />
                    <meta
                        itemProp="longitude"
                        content={longitude}
                    />
                </span>
                <span
                    itemProp="containedInPlace"
                    itemScope
                    itemType="http://schema.org/City"
                >
                    <meta
                        itemProp="name"
                        content={city}
                    />
                    <meta
                        itemProp="url"
                        content={spotUrl.split('-parking')[0]}
                    />
                </span>
            </span>
        );
    }

    render() {
        const {
            spot: {
                selectedRate,
                title,
                activeMarker,
                distance,
                distanceInMiles,
                drivingDuration,
                walkingDuration,
                spotId,
                available_transient_inventory: inventory
            },
            isMonthly,
            showAmenities,
            searchType
        } = this.props;
        const {
            hovered
        } = this.state;
        const {
            amenities,
            unavailable,
            unavailable_reason: unavailableReason
        } = selectedRate;
        const resultType = (isMonthly) ? 'monthly' : 'hourly';
        const containerClasses = classNames(
            'SpotListItemRow',
            `${resultType}-result`,
            `result${spotId}`,
            {'SpotListItemRow-active': activeMarker},
            {'SpotListItemRow-hovered': hovered},
            {unavailable}
        );

        return (
            <div
                className={containerClasses}
                data-spot={spotId}
                itemProp="offers"
                itemScope
                itemType="http://schema.org/Offer"
                onClick={this._onShowDetailsClick}
            >
                {this._renderOfferSchema()}
                <div
                    itemProp="availableAtOrFrom"
                    itemScope
                    itemType="http://schema.org/ParkingFacility"
                >
                    <div className="images">
                        <Image
                            width={this._IMAGE_WIDTH}
                            height={this._IMAGE_HEIGHT}
                            src={this._FORMATTED_IMAGE}
                            itemProp="image"
                            alt={`Photo of ${title}`}
                            lazyLoad
                        />
                    </div>
                    <div className="detail">
                        <h2 className="heading-md">
                            <span itemProp="name">
                                {this._renderControls()}
                                {(!searchType || searchType !== 'city') &&
                                    <Fragment>
                                        {' / '}
                                        <ProximityInformation
                                            amenities={amenities}
                                            distance={distance}
                                            distanceInMiles={distanceInMiles}
                                            drivingDuration={drivingDuration}
                                            walkingDuration={walkingDuration}
                                        />
                                    </Fragment>
                                }
                            </span>
                        </h2>
                        <p className="SpotListItemRow-title">
                            {title}
                        </p>
                        {this._renderSpotSchema()}
                        {showAmenities &&
                            <AmenityIconList
                                isMonthly={isMonthly}
                                rate={selectedRate}
                                pill
                            />
                        }
                        {unavailableReason &&
                            <span className="not-available-reason text-light">
                                <sup>*</sup> {unavailableReason}
                            </span>
                        }
                    </div>
                </div>
                {inventory > 0 && inventory < 4 &&
                    <Alert
                        className="remaining-spots"
                        type="highlight"
                        size="sm"
                    >
                        {inventory} {`spot${(inventory > 1) ? 's' : ''}`} left
                    </Alert>
                }
            </div>
        );
    }
}

export default WithConfig(SpotListItemRow);
